import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Colors,
    Icon,
    Modal,
    type IconPickerOption,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { CARD_SURFACE } from './colorPresets';
import { DeletePerformanceScoreLevelModal } from './DeletePerformanceScoreLevelModal';
import { LevelIconPreview } from './LevelIconPreview';
import { PerformanceScoreLevelCard } from './PerformanceScoreLevelCard';
import {
    createPerformanceScoreLevelFormSchema,
    MAX_PERFORMANCE_SCORE_LEVELS,
    MIN_PERFORMANCE_SCORE_LEVELS,
    type PerformanceScoreLevelFormValues,
} from './performanceScoreLevelSchema';

import type { PerformanceScoreLevel } from '@/types/competency/PerformanceScoreLevel';

import {
    useDeletePerformanceScoreLevel,
    useGetPerformanceScoreLevels,
    useReplacePerformanceScoreLevels,
} from '@/api/competency-services/performance-score-level';
import { PROFICIENCY_ICON_CATALOG } from '@/constants/proficiencyIconCatalog';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { showErrorToast, showSuccessToast } from '@/plugins/Toast';
import { validationMessageKey } from '@/utils/validationMessage';

const sortByMinScoreDesc = (levels: PerformanceScoreLevel[]): PerformanceScoreLevel[] => {
    return [...levels].sort((a, b) => (b.minScore ?? -1) - (a.minScore ?? -1));
};

export const PerformanceScoreLevelsSection = () => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { data, isLoading, isError, refetch } = useGetPerformanceScoreLevels();
    const replaceMutation = useReplacePerformanceScoreLevels();
    const deleteMutation = useDeletePerformanceScoreLevel();

    const [levelToDelete, setLevelToDelete] = useState<PerformanceScoreLevel | null>(null);

    const closeDeleteModal = () => {
        setLevelToDelete(null);
    };

    const confirmDeleteLevel = (replacementLevelId?: string) => {
        if (!levelToDelete?.id) {
            return;
        }

        deleteMutation.mutate(
            { id: levelToDelete.id, replacementLevelId },
            {
                onSuccess: () => {
                    showSuccessToast({
                        message: t('competencies.performanceScoreLevels.delete.deleted'),
                    });
                    closeDeleteModal();
                },
                onError: () => {
                    showErrorToast({
                        message: t('competencies.performanceScoreLevels.delete.deleteError'),
                    });
                },
            },
        );
    };

    const formSchema = useMemo(() => createPerformanceScoreLevelFormSchema(language), [language]);

    const {
        formState: { errors, isDirty },
        getValues,
        handleSubmit,
        reset,
        setValue,
        trigger,
        watch,
    } = useForm<PerformanceScoreLevelFormValues>({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { levels: [] },
    });
    const levels = watch('levels');

    useEffect(() => {
        if (!data) {
            return;
        }

        const sorted = sortByMinScoreDesc(data);
        reset({ levels: sorted });
    }, [data, reset]);

    const iconOptions = useMemo<IconPickerOption[]>(
        () =>
            PROFICIENCY_ICON_CATALOG.map((entry) => ({
                value: entry.value,
                label: t(entry.labelKey),
                preview: (
                    <LevelIconPreview
                        icon={entry.value}
                        className={'h-5 w-5'}
                    />
                ),
            })),
        [t],
    );

    const updateLevel = (index: number, patch: Partial<PerformanceScoreLevel>) => {
        const currentLevels = getValues('levels');
        setValue(
            'levels',
            currentLevels.map((level, i) => (i === index ? { ...level, ...patch } : level)),
            { shouldValidate: true, shouldDirty: true },
        );
    };

    const addLevel = () => {
        const currentLevels = getValues('levels');
        if (currentLevels.length >= MAX_PERFORMANCE_SCORE_LEVELS) {
            return;
        }

        const emptyLocalized: Record<string, string> = {};

        setValue(
            'levels',
            [
                ...currentLevels,
                {
                    id: null,
                    icon: 'gauge',
                    color: Colors.PRIMARY,
                    label: emptyLocalized,
                    description: emptyLocalized,
                    minScore: undefined,
                    sortOrder: currentLevels.length,
                },
            ],
            { shouldValidate: true, shouldDirty: true },
        );
    };

    const removeLevel = (index: number) => {
        const currentLevels = getValues('levels');
        const level = currentLevels[index];

        if (level?.id) {
            setLevelToDelete(level);
            return;
        }

        if (currentLevels.length <= MIN_PERFORMANCE_SCORE_LEVELS) {
            return;
        }

        setValue(
            'levels',
            currentLevels.filter((_, i) => i !== index),
            { shouldValidate: true, shouldDirty: true },
        );
    };

    const onSubmit = async (values: PerformanceScoreLevelFormValues) => {
        const sortedLevels = sortByMinScoreDesc(values.levels).map((level, index) => ({
            ...level,
            sortOrder: index,
        }));

        setValue('levels', sortedLevels, { shouldDirty: true });
        const isValid = await trigger();
        if (!isValid) {
            return;
        }

        replaceMutation.mutate(
            { levels: sortedLevels },
            {
                onSuccess: (saved: PerformanceScoreLevel[]) => {
                    const hydrated = sortByMinScoreDesc(saved).map((level, index) => ({
                        ...level,
                        sortOrder: index,
                    }));
                    reset({ levels: hydrated });
                    showSuccessToast({ message: t('competencies.performanceScoreLevels.saved') });
                },
                onError: () =>
                    showErrorToast({ message: t('competencies.performanceScoreLevels.saveError') }),
            },
        );
    };

    const getLevelErrorMessages = (index: number) => {
        const levelErrors = errors.levels?.[index];
        if (!levelErrors || typeof levelErrors === 'string') {
            return {};
        }

        const getMessage = (message: unknown) =>
            typeof message === 'string' ? t(validationMessageKey(message)) : undefined;

        return {
            title: getMessage(levelErrors.label?.message),
            description: getMessage(levelErrors.description?.message),
            icon: getMessage(levelErrors.icon?.message),
            color: getMessage(levelErrors.color?.message),
        };
    };

    if (isLoading) {
        return (
            <div className={CARD_SURFACE}>
                <p className={'py-8 text-center text-sm text-[#5E5E5E]'}>
                    {t('competencies.performanceScoreLevels.loading')}
                </p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className={CARD_SURFACE}>
                <div className={'flex flex-col items-center gap-4 py-8 text-center'}>
                    <p className={'text-sm text-[#ef4444]'}>
                        {t('competencies.performanceScoreLevels.loadError')}
                    </p>

                    <button
                        type={'button'}
                        onClick={() => refetch()}
                        className={
                            'text-navy flex h-11 items-center rounded-lg border border-gray-300 bg-white px-6 text-sm font-medium transition-colors hover:bg-[#f5f7f9]'
                        }
                    >
                        {t('competencies.performanceScoreLevels.retry')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={'mt-12 border-t border-gray-200 pt-8'}>
            <div className={'mb-8'}>
                <h2 className={'text-navy text-subtitle mb-2 font-semibold'}>
                    {t('competencies.performanceScoreLevels.title')}
                </h2>

                <p className={'text-body-sm text-gray-500'}>
                    {t('competencies.performanceScoreLevels.subtitle')}
                </p>
            </div>

            {errors.levels?.message && typeof errors.levels.message === 'string' && (
                <p className={'mb-4 text-sm text-[#ef4444]'}>
                    {t(validationMessageKey(errors.levels.message))}
                </p>
            )}

            <div className={'flex flex-col gap-3'}>
                {levels.map((level, index) => (
                    <PerformanceScoreLevelCard
                        key={level.id ?? String(index)}
                        index={index}
                        level={level}
                        language={language}
                        iconOptions={iconOptions}
                        errorMessages={getLevelErrorMessages(index)}
                        canRemove={levels.length > MIN_PERFORMANCE_SCORE_LEVELS}
                        onUpdate={updateLevel}
                        onRemove={removeLevel}
                    />
                ))}
            </div>

            <button
                type={'button'}
                onClick={addLevel}
                disabled={levels.length >= MAX_PERFORMANCE_SCORE_LEVELS}
                className={
                    'text-navy mt-4 flex h-11 items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 text-sm font-medium transition-colors hover:bg-[#f5f7f9] disabled:cursor-not-allowed disabled:opacity-40'
                }
                data-testid={'performance-levels-add'}
            >
                <span className={'text-lg leading-none'}>+</span>

                {t('competencies.performanceScoreLevels.addLevel')}
            </button>

            <div className={'mt-8 flex items-center gap-3'}>
                <ButtonGradientPrimary
                    size={'md'}
                    onClick={handleSubmit(onSubmit)}
                    disabled={replaceMutation.isPending || deleteMutation.isPending || !isDirty}
                    dataTestId={'performance-levels-save'}
                    leftIcon={
                        <Icon
                            name={'archive-save-add'}
                            size={'md'}
                            color={Colors.WHITE}
                        />
                    }
                >
                    {replaceMutation.isPending
                        ? t('competencies.performanceScoreLevels.saving')
                        : t('competencies.performanceScoreLevels.save')}
                </ButtonGradientPrimary>

                {isDirty && (
                    <ButtonSecondary
                        onClick={() => reset({ levels: data ? sortByMinScoreDesc(data) : [] })}
                        dataTestId={'performance-levels-cancel'}
                        size={'md'}
                        className={'sm:text-md text-sm'}
                        fitContent={true}
                        leftIcon={
                            <Icon
                                name={'arrow-refresh'}
                                size={'md'}
                            />
                        }
                    >
                        {t('competencies.performanceScoreLevels.cancel')}
                    </ButtonSecondary>
                )}
            </div>

            {levelToDelete && (
                <Modal
                    isOpen={true}
                    onClose={closeDeleteModal}
                    className={'w-[560px] max-w-[92vw]'}
                    dataTestId={'performance-level-delete-modal'}
                    zIndex={50}
                >
                    <DeletePerformanceScoreLevelModal
                        level={levelToDelete}
                        levels={data ? sortByMinScoreDesc(data) : []}
                        isDeleting={deleteMutation.isPending}
                        onClose={closeDeleteModal}
                        onConfirm={confirmDeleteLevel}
                    />
                </Modal>
            )}
        </div>
    );
};
